<template>
  <Container
    id="heading"
  >
    <HeroCard
      :heading="t('jc.view.infraDeveloper.hero.title')"
      :paragraph="t('jc.view.infraDeveloper.hero.text')"
      :image="{
        src: banner,
        alt: t('jc.view.infraDeveloper.hero.banner.alt'),
      }"
      :float="{
        src: float,
        alt: t('jc.view.infraDeveloper.hero.float.alt'),
      }"
      :label="t('jc.view.infraDeveloper.hero.button.label')"
      :icon="{ prefix: 'fab', iconName: 'discord' }"
      :to="config.public.discordParticipateUrl"
    />
  </Container>

  <Container
    id="our-offer"
  >
    <ContentCard
      :heading="t('jc.view.infraDeveloper.ourOffer.title')"
      :paragraph="t('jc.view.infraDeveloper.ourOffer.text')"
    >
      <IconWidget
        :items="ourOfferHighlightItems"
        class="my-12"
      />
      <IconList
        :items="ourOfferItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="our-conditions"
  >
    <ContentCard
      :heading="t('jc.view.infraDeveloper.ourConditions.title')"
      :paragraph="t('jc.view.infraDeveloper.ourConditions.text')"
    >
      <IconList
        :items="ourConditionsItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="your-tasks"
  >
    <ContentCard
      :heading="t('jc.view.infraDeveloper.yourTasks.title')"
      :paragraph="t('jc.view.infraDeveloper.yourTasks.text')"
    >
      <IconList
        :items="yourTasksItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="impressions"
  >
    <ContentCard
      :heading="t('jc.view.infraDeveloper.impressions.title')"
      :paragraph="t('jc.view.infraDeveloper.impressions.text')"
    >
      <div
        class="grid grid-cols-1 laptop:grid-cols-2 gap-x-4 gap-y-8"
      >
        <template
          v-for="({ identifier, ...resource }, index) in impressionsResources"
          :key="index"
        >
          <DynamicResource
            :resource="resource"
            :caption="t(`jc.view.infraDeveloper.impressions.${identifier}.text`)"
            aspect-video
            rounded
          />
        </template>
      </div>
    </ContentCard>
  </Container>

  <Container
    id="how-to-apply"
  >
    <ContentCard
      :heading="t('jc.view.infraDeveloper.howToApply.title')"
      :paragraph="t('jc.view.infraDeveloper.howToApply.text')"
    >
      <IconSection
        :sections="howToApplySections"
      />
    </ContentCard>
  </Container>

  <Container
    id="more-information"
  >
    <div
      class="flex flex-col gap-8"
    >
      <ContentCard
        :heading="t('jc.view.infraDeveloper.moreInformation.title')"
        :paragraph="t('jc.view.infraDeveloper.moreInformation.text')"
      />
      <div
        class="grid grid-cols-1 mobile:gird-cols-2 laptop:grid-cols-3 desktop:grid-cols-4 gap-x-4 gap-y-16 justify-center"
      >
        <template
          v-for="(card, index) in moreInformationCards"
          :key="index"
        >
          <ShowcaseCard
            v-bind="card"
          />
        </template>
      </div>
    </div>
  </Container>

  <Container
    id="callToAction"
    :background="'white'"
  >
    <ParticipateCallCard />
  </Container>
</template>

<script setup lang="ts">
const banner = 'images/component/participateCallCard/background.png'
const float = 'images/skin_renders/roles/infra_developer.png'

definePageMeta({
  nameKey: 'participate-infraDeveloper',
})

const { t } = useI18n()
const localePath = useLocalePath()
const config = useRuntimeConfig()

const ourOfferHighlightItems = [
  {
    icon: 'users',
    text: t('jc.view.infraDeveloper.ourOffer.iconHighlight.atmosphere.text'),
  },
  {
    icon: 'brain-circuit',
    text: t('jc.view.infraDeveloper.ourOffer.iconHighlight.newTechniques.text'),
  },
  {
    icon: 'flask-round-potion',
    text: t('jc.view.infraDeveloper.ourOffer.iconHighlight.innovativeConcepts.text'),
  },
  {
    icon: 'clock-rotate-left',
    text: t('jc.view.infraDeveloper.ourOffer.iconHighlight.versioning.text'),
  },
  {
    icon: 'microchip',
    text: t('jc.view.infraDeveloper.ourOffer.iconHighlight.modernTools.text'),
  },
  {
    icon: 'campground',
    text: t('jc.view.infraDeveloper.ourOffer.iconHighlight.teamEvents.text'),
  },
]

const ourOfferItems = [
  {
    icon: 'shield-check',
    text: t('jc.view.infraDeveloper.ourOffer.icon.securities.text'),
  },
  {
    icon: 'brain-arrow-curved-right',
    text: t('jc.view.infraDeveloper.ourOffer.icon.hierarchy.text'),
  },
  {
    icon: 'sitemap',
    text: t('jc.view.infraDeveloper.ourOffer.icon.hierarchy.text'),
  },
  {
    icon: 'clock',
    text: t('jc.view.infraDeveloper.ourOffer.icon.timeManagement.text'),
  },
  {
    icon: 'network-wired',
    text: t('jc.view.infraDeveloper.ourOffer.icon.dynamicInfrastructure.text'),
  },
  {
    icon: 'monitor-waveform',
    text: t('jc.view.infraDeveloper.ourOffer.icon.monitoring.text'),
  },
  {
    icon: 'shield-heart',
    text: t('jc.view.infraDeveloper.ourOffer.icon.stableSystems.text'),
  },
  {
    icon: 'magnifying-glass',
    text: t('jc.view.infraDeveloper.ourOffer.icon.codeReview.text'),
  },
  {
    icon: 'server',
    text: t('jc.view.infraDeveloper.ourOffer.icon.hardware.text'),
  },
  {
    icon: 'toolbox',
    text: t('jc.view.infraDeveloper.ourOffer.icon.teamTools.text'),
  },
]

const ourConditionsItems = [
  {
    icon: 'couch',
    text: t('jc.view.infraDeveloper.ourConditions.icon.teamSpirit.text'),
  },
  {
    icon: 'dna',
    text: t('jc.view.infraDeveloper.ourConditions.icon.creativity.text'),
  },
  {
    icon: 'book-open-reader',
    text: t('jc.view.infraDeveloper.ourConditions.icon.learn.text'),
  },
  {
    icon: 'gauge-max',
    text: t('jc.view.infraDeveloper.ourConditions.icon.motivation.text'),
  },
  {
    icon: 'comment-smile',
    text: t('jc.view.infraDeveloper.ourConditions.icon.communication.text'),
  },
  {
    icon: 'battery-full',
    text: t('jc.view.infraDeveloper.ourConditions.icon.endurance.text'),
  },
]

const yourTasksItems = [
  {
    icon: 'code-merge',
    text: t('jc.view.infraDeveloper.yourTasks.icon.add.text'),
  },
  {
    icon: 'camera-cctv',
    text: t('jc.view.infraDeveloper.yourTasks.icon.monitor.text'),
  },
  {
    icon: 'gauge-max',
    text: t('jc.view.infraDeveloper.yourTasks.icon.optimise.text'),
  },
  {
    icon: 'shield-plus',
    text: t('jc.view.infraDeveloper.yourTasks.icon.enhance.text'),
  },
  {
    icon: 'microscope',
    text: t('jc.view.infraDeveloper.yourTasks.icon.test.text'),
  },
  {
    icon: 'info',
    text: t('jc.view.infraDeveloper.yourTasks.icon.complement.text'),
  },
]

const impressionsResources = [
  {
    identifier: 'hub',
    mediaType: 'image/*',
    src: 'images/impressions/general/hub.png',
  },
  {
    identifier: 'clashlandsHub',
    mediaType: 'image/*',
    src: 'images/impressions/general/pvp_hub.png',
  },
  {
    identifier: 'dungeonRoom',
    mediaType: 'image/*',
    src: 'images/impressions/general/dungeon_room.png',
  },
  {
    identifier: 'conquestMap',
    mediaType: 'image/*',
    src: 'images/impressions/general/conquest_map.png',
  },
  {
    identifier: 'cosmeticMenu',
    mediaType: 'image/*',
    src: 'images/impressions/general/cosmetic_menu.png',
  },
  {
    identifier: 'clashlandsCannon',
    mediaType: 'image/*',
    src: 'images/impressions/general/cannon.png',
  },
  {
    identifier: 'lens',
    mediaType: 'image/*',
    src: 'images/impressions/infra_developer/lens.png',
  },
  {
    identifier: 'mongodb',
    mediaType: 'image/*',
    src: 'images/impressions/infra_developer/mongo_db.png',
  },
]

const howToApplySections = [
  {
    icon: 'hand-wave',
    title: t('jc.view.infraDeveloper.howToApply.icon.firstContact.title'),
    text: t('jc.view.infraDeveloper.howToApply.icon.firstContact.text'),
  },
  {
    icon: 'comments',
    title: t('jc.view.infraDeveloper.howToApply.icon.conversation.title'),
    text: t('jc.view.infraDeveloper.howToApply.icon.conversation.text'),
  },
  {
    icon: 'envelope',
    title: t('jc.view.infraDeveloper.howToApply.icon.wait.title'),
    text: t('jc.view.infraDeveloper.howToApply.icon.wait.text'),
  },
  {
    icon: 'user-graduate',
    title: t('jc.view.infraDeveloper.howToApply.icon.trainee.title'),
    text: t('jc.view.infraDeveloper.howToApply.icon.trainee.text'),
  },
  {
    icon: 'comment-smile',
    title: t('jc.view.infraDeveloper.howToApply.icon.feedbackTalk.title'),
    text: t('jc.view.infraDeveloper.howToApply.icon.feedbackTalk.text'),
  },
]

const moreInformationCards = [
  {
    icon: 'users',
    text: t('jc.view.infraDeveloper.moreInformation.card.team.text'),
    label: t('jc.view.infraDeveloper.moreInformation.card.team.button.label'),
    to: localePath({ name: 'team' }),
  },
  {
    icon: 'newspaper',
    text: t('jc.view.infraDeveloper.moreInformation.card.weekly_review.text'),
    label: t('jc.view.infraDeveloper.moreInformation.card.weekly_review.button.label'),
    to: localePath({ name: 'news' }),
  },
  {
    icon: 'signs-post',
    text: t('jc.view.infraDeveloper.moreInformation.card.plan.text'),
    label: t('jc.view.infraDeveloper.moreInformation.card.plan.button.label'),
    to: localePath({ name: 'game' }),
  },
  {
    icon: 'microchip',
    text: t('jc.view.infraDeveloper.moreInformation.card.stackShare.text'),
    label: t('jc.view.infraDeveloper.moreInformation.card.stackShare.button.label'),
    to: config.public.stackShareUrl,
  },
  {
    icon: 'cube',
    text: t('jc.view.infraDeveloper.moreInformation.card.planetMinecraft.text'),
    label: t('jc.view.infraDeveloper.moreInformation.card.planetMinecraft.button.label'),
    to: config.public.planetMinecraftUrl,
  },
  {
    icon: 'file-lines',
    text: t('jc.view.infraDeveloper.moreInformation.card.blog.text'),
    label: t('jc.view.infraDeveloper.moreInformation.card.blog.button.label'),
    to: config.public.scrayosUrl,
  },
]
</script>
